<template>
  <InfuseModal :title="t('e_connector.networkProfiles.modal.update.title')" @close="emit('close')">
    <form ref="form" id="update-network-profile" @submit.prevent="onSave()">
      <div class="mb-3 flex max-h-[475px] max-w-[965px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.networkProfile.name')" class="basis-full" size="small">
            <InfuseInput
              size="small"
              v-model="networkProfileData.name"
              :maxlength="255"
              :placeholder="t('e_connector.networkProfiles.modal.field.name.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div>
          <div>
            <h3 class="text-sm text-neutral-800">{{ t('e_connector.common.networkProfile.ipRange') }}</h3>
            <p class="text-xs text-neutral-400">
              <i18n-t keypath="e_connector.networkProfiles.modal.description">
                <a :href="wikiLink" class="underline" target="_blank">CIDR</a>
              </i18n-t>
            </p>
          </div>

          <div class="flex flex-col items-start gap-4 pt-2">
            <div v-for="(ipRange, index) in ipRanges" class="flex w-full items-start gap-2" :key="ipRange.id">
              <InfuseField
                class="basis-full"
                size="small"
                :error="ipRange.error ? t('e_connector.networkProfiles.modal.field.ipRange.error.invalidIpRange') : ''"
              >
                <InfuseInput
                  :id="`ip-range-${ipRange.id}`"
                  :placeholder="t('e_connector.networkProfiles.modal.field.ipRange.placeholder')"
                  size="small"
                  v-model="ipRange.ipCidrRange"
                  @update:model-value="onChangeIpRange(ipRange, $event)"
                  required
                />
              </InfuseField>

              <div v-if="index !== 0" class="flex-grow self-stretch pb-1">
                <InfuseButton
                  size="medium"
                  color="neutral"
                  type="button"
                  @click="onRemoveIpRange(ipRange)"
                  class="h-full"
                >
                  <font-awesome-icon :icon="['fas', 'fa-trash']" />
                </InfuseButton>
              </div>
            </div>

            <InfuseButton class="w-full" size="small" color="secondary" type="button" @click="onAddIpRange">
              <font-awesome-icon :icon="['fas', 'fa-plus']" />
            </InfuseButton>
          </div>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton type="submit" form="update-network-profile" :disabled="loading" :loading="loading">
        {{ t('e_connector.common.action.update') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseField, InfuseInput, InfuseModal } from '@infuse/ui/components';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CreateNetworkProfileIpRange } from '@infuse/api/e-connector/network-profile';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { isCidr } from '@infuse/e-connector/network-profile/utils';
import { NetworkProfile } from '@infuse/e-connector/network-profile/domain';
import { cloneDeep } from 'lodash';
import { IpRangeListItem, toIpRangesWithoutAddedFields } from './ip-range-list-item';

interface Props {
  loading: boolean;
  networkProfile: NetworkProfile;
}

interface Emits {
  (e: 'close'): void;
  (e: 'update', networkProfile: NetworkProfile): void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const networkProfileData = ref<NetworkProfile>(cloneDeep(props.networkProfile));

let lastId = 0;
const wikiLink = 'https://en.wikipedia.org/wiki/Classless_Inter-Domain_Routing';

const ipRanges = ref<IpRangeListItem[]>(
  networkProfileData.value.ipRanges.map((ir) => {
    return { id: lastId++, error: false, ipCidrRange: ir.ipCidrRange };
  }) || []
);

function onAddIpRange() {
  ipRanges.value.push({ id: lastId++, error: false, ipCidrRange: '' });
}

function onChangeIpRange(listItem: IpRangeListItem, ipCidrRange: string) {
  listItem.error = false; // reset error
  ipCidrRange = ipCidrRange.trim(); // trim input

  if (isValidIpRange({ ipCidrRange })) {
    listItem.ipCidrRange = ipCidrRange;
  } else {
    listItem.error = true;
  }
}

function onRemoveIpRange(listItem: IpRangeListItem) {
  ipRanges.value = ipRanges.value.filter((ipRange) => ipRange.id !== listItem.id);
}

function isValidIpRange(ipRange: CreateNetworkProfileIpRange): boolean {
  return !(!isCidr(ipRange.ipCidrRange) && ipRange.ipCidrRange?.trim() !== '');
}

function onSave() {
  ipRanges.value = ipRanges.value.filter((ipRange) => ipRange.ipCidrRange?.trim() !== ''); // remove empty IP ranges
  if (ipRanges.value.length === 0 || ipRanges.value.some((ir) => ir.error)) {
    return;
  }

  networkProfileData.value.ipRanges = toIpRangesWithoutAddedFields(ipRanges.value); // assign local ipRanges to updated profile

  emit('update', networkProfileData.value);
}
</script>
